/**
 * Dropdown Component
 */

import React, { FC, useState } from 'react';
import { withTheme } from 'styled-components';
import { DropdownProps, Options, OptionGroups, DropdownColorSchemes } from './dropdown.types';
import Select, { ValueType, ActionMeta, OptionTypeBase } from 'react-select';
import { DesktopCustomStyles, CustomOption, CustomIndicator, RequiredWrapper, Wrapper } from './dropdown.style';
import COLORS from '../../style/universal.color';
import color from 'color';
import useControlledState from '../hooks/useControlledState/useControlledState';

// Retrieve the first option from the array
// Takes option groups into consideration
const retrieveFirstOption: Function = (options: Options[] | OptionGroups[] ): Options => {
  if ('options' in options[0]) {
    return options[0].options[0];
  } else {
    return options[0];
  }
};

// How the custom color config is to be structured
interface ColorConfig {
  highlightColor: string;
  baseTextColor: string;
  hoverColor: string;
  multiValueColor: string;
  multiValueFontColor: string;
}

// Retrieve the color config for this dropdown and create highlight colors
const retrieveColorConfig: Function = (colorScheme: DropdownColorSchemes, dark: boolean, teamColor: string) => {
  const highlightColor: string = retrieveHighlightColor(colorScheme, dark, teamColor);
  const highlightColorLight: string = color(highlightColor).mix(color('white'), 0.95).hex();
  return {
    highlightColor: highlightColor,
    baseTextColor: dark ? COLORS.mlbGrey1 : COLORS.mlbBlack,
    hoverColor: dark ? COLORS.mlbGrey6 : highlightColorLight,
    multiValueColor: dark ? highlightColor : highlightColorLight,
    multiValueFontColor: dark ? COLORS.mlbWhite : highlightColor,
  };
};

// Retrieve the base highlight color
const retrieveHighlightColor: Function = (
  colorType: DropdownColorSchemes,
  dark: boolean,
  teamColor: string
): string => {
  switch(colorType) {
    case DropdownColorSchemes.TICKET_LINK_GREEN:
      return COLORS.ticketGreenHover;
    case DropdownColorSchemes.MLB_TV_ORANGE:
      return COLORS.mlbTvOrangeHover;
    case DropdownColorSchemes.TEAM:
      return teamColor || (dark ? COLORS.linkBlueOnDark : COLORS.linkBlue);
    default:
      return dark ? COLORS.linkBlueOnDark : COLORS.linkBlue;
  }
};

interface CustomComponent {
  [component: string]: FC;
}

// Ensure that an object is an array
const ensureArray: Function = (arr: any): any[] => Array.isArray(arr) ? arr : [arr];

const Dropdown: FC<DropdownProps> = ({
  multiple = false,
  searchable = false,
  disabled = false,
  width = '100%',
  autoSelect = false,
  placeholder = 'Select',
  clearable = false,
  options,
  colorScheme = DropdownColorSchemes.LINK_BLUE,
  required = false,
  handlers = {},
  theme = {},
  selected,
  defaultSelected,
  menuWidth,
  requiredText = 'Required',
  dataAttr = {},
  className
}: DropdownProps) => {

  // Retrieve theme values
  const { dark = false, teamColor1, fonts } = theme;
  const fontFamily = fonts?.family?.primary;

  // Establish the first set values
  const propsDefaultSelected: any[] = defaultSelected || (autoSelect ? retrieveFirstOption(options) : []);

  // Set the selected state
  const [value, setValue] = useControlledState({
    controlled: selected,
    default: propsDefaultSelected,
    name: 'Dropdown',
  });
  const [open, setOpen] = useState<boolean>(false);

  const { onChange, onMenuOpen, onMenuClose, ...selectionHandlers } = handlers;

  // Set up custom change handling payload and set new values
  const handleChange: Function = (selectedOption: OptionTypeBase[], e: any) => {
    const selectedOptionArr: OptionTypeBase[] = ensureArray(selectedOption);
    if(!multiple && (value[0]?.value === selectedOptionArr[0].value)) {
      return;
    }
    setValue(selectedOptionArr);
    if (onChange) {
      onChange({
        changes: multiple ? e.option : selectedOption,
        selected: selectedOption,
        event: e.action
      });
    };
  };

  // Toggle the menu open and close handlers
  const toggleMenu: Function = (opened: boolean) => {
    if (!open && opened && onMenuOpen) {
      onMenuOpen();
    } else if (open && !opened && onMenuClose) {
      onMenuClose();
    }
    setOpen(opened);
  };

  // Custom components to override default react-select components
  const custComps: CustomComponent = {
    Option: CustomOption,
    DropdownIndicator: CustomIndicator
  };

  // Establish if there is a value selected
  const valueSelected: boolean = !!(value && value.length);

  // Retrieve the config for the passed color scheme
  const colorConfig: ColorConfig = retrieveColorConfig(colorScheme, dark, teamColor1);

  const {
    highlightColor,
    baseTextColor,
    hoverColor,
    multiValueColor,
    multiValueFontColor
  } = colorConfig;

  return (
    <Wrapper { ...dataAttr } className={className}>
      <Select
        { ...selectionHandlers }
        components={ custComps }
        styles={ DesktopCustomStyles }
        fontFamily={ fontFamily }
        isClearable={ clearable }
        menuIsOpen={ open }
        highlightColor={ highlightColor }
        baseTextColor={ baseTextColor }
        hoverColor={ hoverColor }
        multiValueColor={ multiValueColor }
        multiValueFontColor={ multiValueFontColor }
        invalidRequired={required && !valueSelected }
        value={ value }
        isMulti={ multiple }
        isDisabled={ disabled }
        options={ options }
        placeholder={ placeholder }
        onChange={ (selectedOption: ValueType<OptionTypeBase, boolean>, e: ActionMeta<OptionTypeBase>) => handleChange((selectedOption as OptionTypeBase[]), e) }
        onMenuOpen={ () => toggleMenu(true) }
        onMenuClose={ () => toggleMenu(false) }
        dark={ dark }
        width={ width }
        menuWidth={ menuWidth || width }
        isSearchable={ searchable }
        hideSelectedOptions={ false }
        closeMenuOnSelect={ !multiple }
      />
      {required && <RequiredWrapper valueSelected={valueSelected} dark={dark} isDisabled={disabled}> { `* ${requiredText}` } </RequiredWrapper> }
    </Wrapper>
  );
};

export default withTheme(Dropdown);
